import isNil from 'lodash/isNil';
import { Fragment, useState } from 'react';
import { useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { Banner } from 'ui/banner';
import { BannerVariants } from 'ui/banner/types';
import { BUTTON_SIZES, RefreshButton } from 'ui/button';
import { InfoFill } from 'ui/icons';
import { Link } from 'ui/links';
import { spacing } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';

import { useIsModernizedSegmentationPageEnabled } from 'modules/segmentation/hooks';
import { useCampaignContext, useEditorContext } from 'modules/campaigns/editor/context';
import { CampaignEditorView } from 'modules/campaigns/editor/types';
import { getRecipientsDisplayText } from 'modules/campaigns/editor/utils';
import { useCampaignSendSize } from 'modules/campaigns/lib/hooks';
import { AudienceList } from 'modules/domains/campaign';

import { CampaignFormField, ReadOnlyField } from '../../FormFields';
import {
  LightText,
  SectionHeader,
  SectionWrapper,
  SpaceSeparatedList,
  SpaceSeparatedListItem,
} from '../Sections.styled';

export const AUDIENCE_ELIGIBILITY_SECTION_TEST_ID = 'audience-eligibility-section';
export const AUDIENCE_ELIGIBILITY_REFRESH_BUTTON = 'audience-eligiblity-refresh-button';

const StyledBanner = styled(Banner)`
  margin-top: ${spacing.base};
`;

export const AudienceEligibilitySection = () => {
  const [isBannerShowing, setIsBannerShowing] = useState<boolean>(true);

  const { estimatedSendSizeData, expectedSendSizeData, refetch } = useCampaignContext();
  // This will need to be updated to be computed dynamically from the eligibility service
  const { sendSize, isEstimated } = useCampaignSendSize({
    estimatedSendSizeData,
    expectedSendSizeData,
  });
  const { campaignEditorView } = useEditorContext();
  const isModernizedSegmentationPageEnabled = useIsModernizedSegmentationPageEnabled();
  const isEditingCampaign = campaignEditorView === CampaignEditorView.Edit;

  const audienceLists = useWatch({ name: 'audienceLists' });
  const { sendLists, useSendLists } = audienceLists;

  const LabelWrapper = sendSize ? Fragment : LightText;

  const recipientsContent = isNil(sendSize) ? (
    <LightText>{getRecipientsDisplayText(0, false)}</LightText>
  ) : (
    <>{getRecipientsDisplayText(sendSize, isEstimated)}</>
  );

  return (
    <SectionWrapper data-test={AUDIENCE_ELIGIBILITY_SECTION_TEST_ID}>
      {!useSendLists && isBannerShowing && (
        <StyledBanner
          data-test="campaign-audience-info-banner"
          onClose={() => setIsBannerShowing(false)}
          Icon={<InfoFill />}
          variant={BannerVariants.INFO}
        >
          To be eligible to see this campaign, users must match the segmentation criteria of its
          associated dynamic list. This check is performed in real time, as they use the app or
          website that can display the message.
        </StyledBanner>
      )}
      <SectionHeader>{!isEditingCampaign && 'Audience'}</SectionHeader>

      <CampaignFormField labelText="Initial eligible recipients">
        <ReadOnlyField name="recipients">
          <LabelWrapper>{recipientsContent}</LabelWrapper>
          {useSendLists && (
            <Tooltip tooltipContent="Refresh estimate">
              <RefreshButton
                data-test={AUDIENCE_ELIGIBILITY_REFRESH_BUTTON}
                onClick={refetch}
                size={BUTTON_SIZES.MEDIUM}
              />
            </Tooltip>
          )}
        </ReadOnlyField>
      </CampaignFormField>

      <CampaignFormField labelText="Eligibility list">
        <ReadOnlyField name="eligibilityLists">
          <SpaceSeparatedList>
            {useSendLists ? (
              sendLists.map(({ name, id }: AudienceList) => (
                <SpaceSeparatedListItem key={name}>
                  <Link
                    hasOpenNewTabIcon
                    href={`/segmentation?emailListId=${id}`}
                    useReactRouter={isModernizedSegmentationPageEnabled}
                  >
                    {name}
                  </Link>
                </SpaceSeparatedListItem>
              ))
            ) : (
              <LightText>Not set</LightText>
            )}
          </SpaceSeparatedList>
        </ReadOnlyField>
      </CampaignFormField>
    </SectionWrapper>
  );
};
